import { onMounted, onUnmounted } from 'vue'
import { hasDirtyDraft } from '@/services/project/draft'

function isReloadShortcut(event: KeyboardEvent): boolean {
  if (event.key === 'F5') return true
  return (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r'
}

/**
 * Native "Leave site?" prompt when the tab closes with unsaved Project
 * changes (the per-tab session draft dies with the tab). Browsers ignore any
 * custom text or UI here -- only whether the prompt shows can be decided.
 *
 * Checks both the project ID and dirty flag in drafts, ignoring untitled maps.
 * This covers every workspace in the tab and keeps mars3d out of App.vue's entry chunk.
 * ponytail: the flag trails edits by the 300 ms draft debounce.
 *
 * Reload restores from the draft, so it should not prompt, but
 * beforeunload cannot tell reload from close.
 * ponytail: only keyboard reload (F5 / Ctrl+R / Cmd+R) is exempt; the
 * toolbar/menu reload is undetectable and still prompts for unsaved Projects.
 */
export function useUnsavedProjectGuard() {
  let reloadShortcut = false

  const onKeyDown = (event: KeyboardEvent) => {
    // Bubble phase: a shortcut the app intercepts is already defaultPrevented.
    if (!event.defaultPrevented && isReloadShortcut(event)) reloadShortcut = true
  }
  // A real reload unloads before keyup; any keyup/blur means it did not.
  const clearReloadShortcut = () => {
    reloadShortcut = false
  }
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (reloadShortcut) return
    if (!hasDirtyDraft()) return
    event.preventDefault()
    event.returnValue = ''
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', clearReloadShortcut)
    window.addEventListener('blur', clearReloadShortcut)
    window.addEventListener('beforeunload', onBeforeUnload)
  })
  onUnmounted(() => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', clearReloadShortcut)
    window.removeEventListener('blur', clearReloadShortcut)
    window.removeEventListener('beforeunload', onBeforeUnload)
  })
}
